/**
 * Annotation V2 - Simplified unified state management
 *
 * Key improvements:
 * - Single AnnotationObject type for all drawing types (paths, shapes, textboxes)
 * - No more separate brushstrokes/teacherShapes/drawings arrays
 * - Simplified isOneToOne handling
 * - Each object has objectId (GUID) + type for easy querying
 */

export interface XYCoords {
  x: number;
  y: number;
}

/**
 * Unified annotation object - replaces brushstrokes/shapes/drawings
 */
export interface AnnotationObject {
  objectId: string; // GUID - unique identifier
  type: string; // 'path' | 'circle' | 'rect' | 'ellipse' | 'triangle' | 'textbox' | 'i-text' etc
  userId: string; // User who created this object
  metadata: string; // JSON serialized FabricJS object data
  isTeacherOnly?: boolean; // For teacher-use-only mode
  timestamp?: number; // Creation timestamp (optional, for sorting/tracking)
}

/**
 * Canvas object update model for real-time updates
 */
export interface CanvasObjectUpdate {
  id: string;
  type: number; // ObjectTypes enum
  operation: number; // OperationTypes enum
  metadata?: string;
}

/**
 * Canvas object update model for real-time updates
 */
export interface CanvasObjectUpdate {
  id: string;
  type: number; // ObjectTypes enum
  operation: number; // OperationTypes enum
  metadata?: string;
}

/**
 * Realtime drawing data (temporary, not persisted until done)
 */
export interface RealtimePencilPath {
  id: string;
  userId?: string;
  points: XYCoords[];
  strokeColor: string;
  strokeWidth: number;
  isDone: boolean;
  ratio: number;
  lineIdRelated: string;
  objectId?: string; // Set when drawing is completed and saved
}

export interface RealtimeLaserPath {
  id: string;
  userId?: string;
  points: XYCoords[];
  strokeColor: string;
  strokeWidth: number;
}

export interface RealtimeDrawing {
  pencilPaths: RealtimePencilPath[];
  laserPaths: RealtimeLaserPath[];
  isLaserDone: boolean;
}

/**
 * Main annotation state - simplified structure
 */
export interface AnnotationStateV2 {
  mode: number; // 0: cursor, 1: pointer mode

  // Single collection for all annotation objects
  objects: AnnotationObject[];

  // Realtime drawing (temporary, in-progress)
  realtimeDrawing: RealtimeDrawing;

  // Real-time object updates (for incremental canvas updates)
  nextObjectTobeUpdated?: CanvasObjectUpdate;

  // Image dimensions for ratio calculations
  imgWidth?: number;
  imgHeight?: number;
}

export const initAnnotationStateV2 = (): AnnotationStateV2 => ({
  mode: 0,
  objects: [],
  realtimeDrawing: {
    pencilPaths: [],
    laserPaths: [],
    isLaserDone: false,
  },
  nextObjectTobeUpdated: undefined,
  imgWidth: undefined,
  imgHeight: undefined,
});

const state: AnnotationStateV2 = initAnnotationStateV2();

export default state;
